import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Empty, FilterGroup, PageHero, SortBar, Stars, toggleIn, useQuery } from '../../shared/components/Common';
import { SmartImg } from '../../shared/components/Layout';
import Icon from '../../shared/components/Icons';
import { formatPrice } from '../../shared/data/common';
import { useHotelSearch } from '../../api/hotels';
import {
  datesLabel, nightsLabel, nightsOf, parseHotelQuery, roomsLabel, searchProblem, shortName, toHotelParams,
  toSearchRequest,
} from './searchModel';
import { freeCancellationText } from './data';

const hasBreakfast = (meal) => /breakfast|board|inclusive/i.test(meal || '');

/** Link to a hotel's page; the search stays in the URL so the page survives a refresh. */
export function hotelPath(search, searchId, hotel) {
  const params = new URLSearchParams({ ...toHotelParams(search), search: searchId, supplier: hotel.supplierCode, hotel: hotel.hotelId });
  return `/hotels/hotel?${params.toString()}`;
}

/** "Calangute, Goa". */
export const placeLine = (address) => [address?.locality, address?.city].filter((x, i, all) => x && all.indexOf(x) === i).join(', ');

function HotelCard({ hotel, nights, rooms, onOpen }) {
  const { price } = hotel;
  const free = hotel.refundable && hotel.freeCancellationUntil ? freeCancellationText({ refundable: true, freeCancellationUntil: hotel.freeCancellationUntil }) : null;
  return (
    <article className="row-card hotel-card" onClick={onOpen} role="link" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }} aria-label={hotel.name}>
      <div className="row-img">
        <SmartImg src={hotel.imageUrl} alt={hotel.name} />
      </div>
      <div className="row-body">
        <div>
          {hotel.starRating > 0 && <Stars n={hotel.starRating} />}
          <h3>{hotel.name}</h3>
          {placeLine(hotel.address) && <span className="muted small"><Icon name="pin" size={13} /> {placeLine(hotel.address)}</span>}
          {hotel.propertyType && hotel.propertyType !== 'Hotel' && <span className="tag hotel-type">{hotel.propertyType}</span>}
        </div>
        {hotel.amenities.length > 0 && (
          <div className="amenities">{hotel.amenities.slice(0, 5).map((a) => <span key={a} className="tag">{a}</span>)}</div>
        )}
        <ul className="cab-points">
          {hotel.roomName && <li><Icon name="hotels" size={13} /> {hotel.roomName}{hotel.mealBasis ? ` · ${hotel.mealBasis}` : ''}</li>}
          {free ? <li className="text-primary"><Icon name="check" size={13} strokeWidth={2.5} /> {free}</li>
            : <li className="text-coral">Non-refundable</li>}
        </ul>
      </div>
      <div className="row-price">
        {price.strikethrough > price.roomPrice && <s className="muted small">{formatPrice(price.strikethrough + price.convenienceFee)}</s>}
        <b className="price big">{formatPrice(price.total)}</b>
        <span className="muted small">{nightsLabel(nights)}{rooms > 1 ? ` · ${rooms} rooms` : ''}, incl. taxes & fees</span>
        <span className="muted small">{formatPrice(price.perNight)} / night</span>
        {hotel.optionCount > 1 && <span className="muted small">{hotel.optionCount} room options</span>}
        <span className="btn btn-primary">View rooms</span>
      </div>
    </article>
  );
}

export default function HotelList() {
  const q = useQuery();
  const navigate = useNavigate();
  const search = parseHotelQuery(q);
  const problem = searchProblem(search);
  const { loading, loadingMore, data, hotels, error, moreError, loadMore, retry } = useHotelSearch(problem ? null : search, toSearchRequest);
  const [sort, setSort] = useState('price');
  const [stars, setStars] = useState([]);
  const [freeOnly, setFreeOnly] = useState(false);
  const [breakfastOnly, setBreakfastOnly] = useState(false);
  const [types, setTypes] = useState([]);
  const [maxPerNight, setMaxPerNight] = useState(null);

  const nights = nightsOf(search);
  const typeList = useMemo(() => [...new Set(hotels.map((h) => h.propertyType).filter(Boolean))].sort(), [hotels]);
  const priceCap = useMemo(() => Math.ceil(Math.max(0, ...hotels.map((h) => h.price.perNight)) / 500) * 500, [hotels]);
  const list = hotels
    .filter((h) => !stars.length || stars.includes(h.starRating || 0))
    .filter((h) => !freeOnly || (h.refundable && h.freeCancellationUntil))
    .filter((h) => !breakfastOnly || hasBreakfast(h.mealBasis))
    .filter((h) => !types.length || types.includes(h.propertyType))
    .filter((h) => maxPerNight == null || h.price.perNight <= maxPerNight)
    .sort({
      price: (a, b) => a.price.total - b.price.total,
      high: (a, b) => b.price.total - a.price.total,
      stars: (a, b) => (b.starRating || 0) - (a.starRating || 0) || a.price.total - b.price.total,
    }[sort]);
  const clear = () => { setStars([]); setFreeOnly(false); setBreakfastOnly(false); setTypes([]); setMaxPerNight(null); };

  const place = data?.destination?.fullName || search.name || 'your destination';
  const summary = {
    tag: 'Hotels',
    items: [
      ['Destination', place],
      ['Dates', `${datesLabel(search)} · ${nightsLabel(nights)}`],
      ['Rooms & guests', roomsLabel(search.rooms)],
    ],
  };
  const open = (hotel) => navigate(hotelPath(search, data.searchId, hotel), { state: { hotel } });

  return (
    <>
      <PageHero tab="hotels" title={`Hotels in ${shortName(place)}`} query={toHotelParams(search)} summary={summary}
        subtitle={`${datesLabel(search)} · ${nightsLabel(nights)} · ${roomsLabel(search.rooms)}`} />
      <div className="container listing">
        <aside className="filters">
          <div className="filters-head"><b>Filters</b>
            <button type="button" className="link small" onClick={clear}>Clear all</button>
          </div>
          <FilterGroup title="Star rating">
            {[5, 4, 3, 2].map((s) => (
              <Check key={s} label={<Stars n={s} />} checked={stars.includes(s)} onChange={(on) => setStars(toggleIn(stars, s, on))}
                extra={hotels.filter((h) => h.starRating === s).length} />
            ))}
          </FilterGroup>
          {priceCap > 0 && (
            <FilterGroup title={`Price per night: up to ${formatPrice(maxPerNight ?? priceCap)}`}>
              <input type="range" className="range" aria-label="Maximum price per night" min={500} max={priceCap} step={500}
                value={maxPerNight ?? priceCap} onChange={(e) => { const v = Number(e.target.value); setMaxPerNight(v >= priceCap ? null : v); }} />
            </FilterGroup>
          )}
          <FilterGroup title="Room & policy">
            <Check label="Free cancellation" checked={freeOnly} onChange={setFreeOnly} />
            <Check label="Breakfast included" checked={breakfastOnly} onChange={setBreakfastOnly} />
          </FilterGroup>
          {typeList.length > 1 && (
            <FilterGroup title="Property type">
              {typeList.map((t) => (
                <Check key={t} label={t} checked={types.includes(t)} onChange={(on) => setTypes(toggleIn(types, t, on))}
                  extra={hotels.filter((h) => h.propertyType === t).length} />
              ))}
            </FilterGroup>
          )}
          <FilterGroup title="Good to know">
            <ul className="policy-list small-list">
              <li>Prices are for the whole stay and all rooms, including taxes and fees.</li>
              <li>Some hotels charge local fees at check-in; they are shown on the hotel page.</li>
            </ul>
          </FilterGroup>
        </aside>

        <div className="results">
          {problem && <div className="api-message error" role="alert">{problem} Use <b>Modify Search</b> above.</div>}
          {!problem && loading && (
            <div className="api-message" role="status"><span className="spinner sm" /> Finding available hotels in {shortName(place)}…</div>
          )}
          {!problem && error && (
            <div className="api-message error" role="alert">
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={retry}>Try again</button>
            </div>
          )}
          {data?.warnings?.map((w) => <div key={w} className="api-message warning">{w}</div>)}
          {data && (
            <>
              <SortBar count={list.length} label={`hotel${list.length === 1 ? '' : 's'}${data.hasMore ? ' so far' : ''}`} sort={sort} setSort={setSort}
                options={[['price', 'Lowest price'], ['high', 'Highest price'], ['stars', 'Star rating']]} />
              {list.length === 0 && (
                <Empty text={hotels.length
                  ? 'No hotels match your filters.'
                  : data.hasMore ? 'No rooms found in the first hotels checked — load more below.' : 'No hotels have rooms for these dates. Try different dates or another area.'} />
              )}
              {list.map((hotel) => (
                <HotelCard key={`${hotel.supplierCode}:${hotel.hotelId}`} hotel={hotel} nights={nights} rooms={search.rooms.length} onOpen={() => open(hotel)} />
              ))}
              {moreError && <div className="api-message error" role="alert">{moreError}</div>}
              {data.hasMore && (
                <div className="load-more">
                  <button type="button" className="btn btn-outline" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Checking more hotels…' : 'Load more hotels'}
                  </button>
                  <span className="muted small">{data.hotelsInDestination.toLocaleString('en-IN')} hotels in {shortName(place)}; prices are checked a batch at a time.</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
